import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControlLabel,
  Checkbox,
  Stack,
  Link,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const ParticipantFilterModal = ({
  open,
  onClose,
  onApply,
  genderFilterModal,
  shirtTypeFilterModal,
  shirtSizeFilterModal,
  onFilterCheckboxChange,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          maxHeight: { xs: '90vh', sm: '85vh', md: '80vh' },
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderBottom: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
        }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
          }}>
          Filter
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'rgba(255, 255, 255, 0.08)',
            },
          }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: { xs: 0, sm: 0, md: 0 },
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 0, sm: 0, md: 0 },
          },
        }}>
        {/* Filter Section (Gender) */}
        <Accordion
          defaultExpanded
          sx={{
            boxShadow: 'none',
            border: 'none',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              minHeight: { xs: 40, sm: 44, md: 48 },
              '&.Mui-expanded': {
                minHeight: { xs: 40, sm: 44, md: 48 },
              },
            }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              }}>
              Filter
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5 },
              pb: { xs: 1.5, sm: 2, md: 2.5 },
              pt: 0,
            }}>
            <Stack spacing={{ xs: 0.75, sm: 1, md: 1.25 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={genderFilterModal.All}
                    onChange={() => onFilterCheckboxChange('gender', 'All')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    All
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={genderFilterModal.male}
                    onChange={() => onFilterCheckboxChange('gender', 'male')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    Male
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={genderFilterModal.female}
                    onChange={() => onFilterCheckboxChange('gender', 'female')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    Female
                  </Typography>
                }
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Divider
          sx={{
            borderColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
          }}
        />

        {/* Shirt Type Section */}
        <Accordion
          defaultExpanded
          sx={{
            boxShadow: 'none',
            border: 'none',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              minHeight: { xs: 40, sm: 44, md: 48 },
              '&.Mui-expanded': {
                minHeight: { xs: 40, sm: 44, md: 48 },
              },
            }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              }}>
              Shirt Type
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5 },
              pb: { xs: 1.5, sm: 2, md: 2.5 },
              pt: 0,
            }}>
            <Stack spacing={{ xs: 0.75, sm: 1, md: 1.25 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtTypeFilterModal.All}
                    onChange={() => onFilterCheckboxChange('shirtType', 'All')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    All
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtTypeFilterModal['Short Sleeves']}
                    onChange={() => onFilterCheckboxChange('shirtType', 'Short Sleeves')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    Short Sleeves
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtTypeFilterModal.Vest}
                    onChange={() => onFilterCheckboxChange('shirtType', 'Vest')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    Vest
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtTypeFilterModal.Unisex}
                    onChange={() => onFilterCheckboxChange('shirtType', 'Unisex')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    Unisex
                  </Typography>
                }
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Divider
          sx={{
            borderColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
          }}
        />

        {/* Shirt Size Section */}
        <Accordion
          defaultExpanded
          sx={{
            boxShadow: 'none',
            border: 'none',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              minHeight: { xs: 40, sm: 44, md: 48 },
              '&.Mui-expanded': {
                minHeight: { xs: 40, sm: 44, md: 48 },
              },
            }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              }}>
              Shirt Size
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5 },
              pb: { xs: 1.5, sm: 2, md: 2.5 },
              pt: 0,
            }}>
            <Stack spacing={{ xs: 0.75, sm: 1, md: 1.25 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal.All}
                    onChange={() => onFilterCheckboxChange('shirtSize', 'All')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    All
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal.Xs}
                    onChange={() => onFilterCheckboxChange('shirtSize', 'Xs')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    Xs
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal.S}
                    onChange={() => onFilterCheckboxChange('shirtSize', 'S')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    S
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal.M}
                    onChange={() => onFilterCheckboxChange('shirtSize', 'M')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    M
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal.L}
                    onChange={() => onFilterCheckboxChange('shirtSize', 'L')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    L
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal.XL}
                    onChange={() => onFilterCheckboxChange('shirtSize', 'XL')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    XL
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal['2XL']}
                    onChange={() => onFilterCheckboxChange('shirtSize', '2XL')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    2XL
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shirtSizeFilterModal['3XL']}
                    onChange={() => onFilterCheckboxChange('shirtSize', '3XL')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      color: 'text.primary',
                    }}>
                    3XL
                  </Typography>
                }
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderTop: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
          justifyContent: 'flex-end',
          gap: { xs: 1, sm: 1.25, md: 1.5 },
        }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 1,
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            fontWeight: 500,
            textTransform: 'none',
            px: { xs: 1.5, sm: 2, md: 2.5 },
            py: { xs: 1, sm: 1.125, md: 1.25 },
          }}>
          Cancel
        </Button>
        <Button
          onClick={onApply}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 1,
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            fontWeight: 600,
            textTransform: 'none',
            px: { xs: 1.5, sm: 2, md: 2.5 },
            py: { xs: 1, sm: 1.125, md: 1.25 },
          }}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ParticipantFilterModal
